import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import type { FormikErrors } from 'formik';
import * as Yup from 'yup';
import type { TFunction } from 'i18next';

import { Checkbox, Input, Radio, Rate, Space, Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { Colors, NetworkErrors, UserRole } from 'context';
import { Button, ButtonSizes, ToastTypes } from 'atoms';
import {
  appState,
  authState,
  SurveyErrorCode,
  useMySurveys,
  useSubmitSurveyResponse,
  useToaster,
} from 'core';
import type { ISurveyFormValues, ISurveyQuestion, SurveyAnswerValue } from 'core';
import { PATHS } from 'router/config';

const NPS_SCALE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Questions that are ABOUT the lesson's teacher, not the course. */
const TEACHER_QUESTION_KINDS: ReadonlySet<string> = new Set(['teacher_rating', 'teacher_comment']);

/**
 * Untouched widget state per question kind. The typed encoding (bool / string /
 * index / index-list / int) matches the contract's value table; the transform
 * layer JSON.stringify-s it on submit and drops what stays in this state.
 */
const buildInitialValues = (questions: ISurveyQuestion[]): ISurveyFormValues => {
  const values: ISurveyFormValues = {};
  questions.forEach((question) => {
    switch (question.questionType) {
      case 'text':
      case 'teacher_comment':
        values[question.id] = '';
        break;
      case 'rating':
      case 'teacher_rating':
        // antd Rate needs a number; 0 = not rated yet.
        values[question.id] = 0;
        break;
      case 'multi_choice':
        values[question.id] = [];
        break;
      default:
        values[question.id] = null;
    }
  });
  return values;
};

/** Required questions must be answered; optional ones carry no rule at all. */
const buildValidationSchema = (questions: ISurveyQuestion[], t: TFunction) => {
  const message = t('surveys.fill.requiredError');
  const shape: Record<string, Yup.AnySchema> = {};
  questions.forEach((question) => {
    if (!question.required) {
      return;
    }
    switch (question.questionType) {
      case 'yes_no':
        shape[question.id] = Yup.boolean().nullable().required(message);
        break;
      case 'text':
      case 'teacher_comment':
        shape[question.id] = Yup.string().trim().required(message);
        break;
      case 'rating':
      case 'teacher_rating':
        shape[question.id] = Yup.number().min(1, message).required(message);
        break;
      case 'multi_choice':
        shape[question.id] = Yup.array().min(1, message).required(message);
        break;
      // single_choice & nps: any picked value is valid, only presence matters.
      default:
        shape[question.id] = Yup.number().nullable().required(message);
    }
  });
  return Yup.object().shape(shape);
};

interface IQuestionWidget {
  question: ISurveyQuestion;
  value: SurveyAnswerValue | undefined;
  onChange: (value: SurveyAnswerValue) => void;
}

const QuestionWidget = memo(({ question, value, onChange }: IQuestionWidget) => {
  const { t } = useTranslation();

  switch (question.questionType) {
    case 'yes_no':
      return (
        <Radio.Group
          value={value ?? undefined}
          onChange={(event) => onChange(event.target.value as boolean)}>
          {/* `value` shorthand === value={true} (jsx-boolean-value rule). */}
          <Radio value>{t('surveys.fill.yes')}</Radio>
          <Radio value={false}>{t('surveys.fill.no')}</Radio>
        </Radio.Group>
      );
    case 'rating':
    case 'teacher_rating':
      return <Rate value={typeof value === 'number' ? value : 0} onChange={onChange} />;
    case 'single_choice':
      return (
        <Radio.Group
          value={value ?? undefined}
          onChange={(event) => onChange(event.target.value as number)}>
          <Space direction="vertical">
            {question.options.map((option, index) => (
              // Option INDEX is the stored value (contract): renaming an option
              // in the constructor must not orphan collected answers.
              // eslint-disable-next-line react/no-array-index-key
              <Radio key={index} value={index}>
                {option}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      );
    case 'multi_choice':
      return (
        <VerticalCheckboxGroup
          value={Array.isArray(value) ? value : []}
          onChange={(checked) => onChange(checked as number[])}
          options={question.options.map((option, index) => ({ label: option, value: index }))}
        />
      );
    case 'nps':
      return (
        <NpsGroup
          value={value ?? undefined}
          buttonStyle="solid"
          onChange={(event) => onChange(event.target.value as number)}>
          {NPS_SCALE.map((mark) => (
            <Radio.Button key={mark} value={mark}>
              {mark}
            </Radio.Button>
          ))}
        </NpsGroup>
      );
    case 'text':
    case 'teacher_comment':
    default:
      return (
        <Input.TextArea
          rows={4}
          maxLength={2000}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={t('surveys.fill.textPlaceholder')}
        />
      );
  }
});

interface IScrollToFirstError {
  questions: ISurveyQuestion[];
  errors: FormikErrors<ISurveyFormValues>;
  submitCount: number;
  cardRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

/**
 * Brings the first unanswered required question into view after a failed
 * submit. The submit button sits at the bottom of a long form, so without this
 * a missed question near the top renders an inline error the student never
 * sees — and the click reads as a no-op.
 *
 * Rendered inside <Formik> (hooks can't run in the render prop) and returns
 * null. `handledRef` makes it fire once per submit ATTEMPT: the effect also
 * re-runs on every `errors` identity change, and we deliberately do not mark
 * the attempt handled until an invalid question actually exists, because
 * Formik may bump `submitCount` a commit before validation lands.
 */
const ScrollToFirstError = memo(
  ({ questions, errors, submitCount, cardRefs }: IScrollToFirstError) => {
    const handledRef = useRef(0);

    useEffect(() => {
      if (!submitCount || handledRef.current === submitCount) {
        return;
      }
      const firstInvalid = questions.find((question) => errors[question.id]);
      if (!firstInvalid) {
        return;
      }
      handledRef.current = submitCount;

      const card = cardRefs.current[firstInvalid.id];
      if (!card) {
        return;
      }
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Move keyboard focus into the question too, so it is not a purely visual
      // jump for screen-reader / keyboard users.
      card
        .querySelector<HTMLElement>('input, textarea, [tabindex]:not([tabindex="-1"])')
        ?.focus({ preventScroll: true });
    }, [submitCount, questions, errors, cardRefs]);

    return null;
  },
);

/**
 * /surveys/:assignmentId — filling a pending survey (spec 0011). The survey is
 * looked up in the (refetched-on-mount) `mySurveys` payload — it already
 * carries the questions, so there is no per-assignment query. A miss after
 * load means the survey was submitted/detached meanwhile → friendly
 * "no longer available" instead of a form that would 400 on submit.
 */
export const SurveyFill = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assignmentId } = useParams<{ assignmentId: string }>();

  const { active_role, isDemoUser } = useReactiveVar(authState);
  const currentState = appState();
  const { addToast } = useToaster();

  const { surveys, loading, error, refetch } = useMySurveys();
  const { submitSurveyResponse, loading: submitting } = useSubmitSurveyResponse();

  // Per-question card nodes, so a failed submit can scroll to the first one.
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (active_role !== UserRole.STUDENT || isDemoUser) {
      appState({
        ...currentState,
        networkError: NetworkErrors.pageIsNotAvailable,
      });
    }
  }, [active_role, currentState, isDemoUser]);

  // Deep-link hardening: cache-and-network already refires, but an explicit
  // refetch on mount guarantees a just-submitted-elsewhere survey is gone
  // before the student invests time filling it again.
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const survey = useMemo(
    () => surveys.find((pending) => pending.assignmentId === assignmentId),
    [surveys, assignmentId],
  );

  const initialValues = useMemo(
    () => (survey ? buildInitialValues(survey.questions) : {}),
    [survey],
  );
  const validationSchema = useMemo(
    () => (survey ? buildValidationSchema(survey.questions, t) : undefined),
    [survey, t],
  );

  const onSubmit = async (values: ISurveyFormValues) => {
    if (!survey) {
      return;
    }
    try {
      const { success, errorCode } = await submitSurveyResponse(
        survey.assignmentId,
        survey.questions,
        values,
      );

      if (success) {
        addToast('survey_submit', { type: ToastTypes.success, text: t('surveys.toast.success') });
        navigate(PATHS.surveys);
        return;
      }

      // Stale pending state (submitted in another tab / assignment turned
      // off): the hook's refetchQueries already re-syncs list + counter, so
      // just explain and go back to the list.
      if (
        errorCode === SurveyErrorCode.ALREADY_SUBMITTED ||
        errorCode === SurveyErrorCode.NOT_AVAILABLE
      ) {
        addToast('survey_submit', {
          type: ToastTypes.warning,
          text:
            errorCode === SurveyErrorCode.ALREADY_SUBMITTED
              ? t('surveys.toast.alreadySubmitted')
              : t('surveys.toast.notAvailable'),
        });
        navigate(PATHS.surveys);
        return;
      }

      // REQUIRED_MISSING / INVALID_VALUE / unknown — stay on the form.
      addToast('survey_submit', { type: ToastTypes.danger, text: t('surveys.toast.error') });
    } catch {
      // Network error — stay on the form so the answers are not lost.
      addToast('survey_submit', { type: ToastTypes.danger, text: t('surveys.toast.error') });
    }
  };

  if (loading && !survey) {
    return (
      <SurveyPageWrapper>
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      </SurveyPageWrapper>
    );
  }

  if (error || !survey) {
    return (
      <SurveyPageWrapper>
        <SurveyHeader>
          <PageTitle>{t('surveys.unavailable.title')}</PageTitle>
          <PageInfo>{t('surveys.unavailable.text')}</PageInfo>
        </SurveyHeader>
        <BackButton size={ButtonSizes.sm} variant="outline" onClick={() => navigate(PATHS.surveys)}>
          {t('surveys.unavailable.back')}
        </BackButton>
      </SurveyPageWrapper>
    );
  }

  return (
    <SurveyPageWrapper>
      <SurveyHeader>
        <PageTitle>{survey.title}</PageTitle>
        {survey.description ? <PageInfo>{survey.description}</PageInfo> : null}
        <PageMeta>
          {survey.groupName} · {t('surveys.card.afterLesson', { n: survey.lessonNumber })}
        </PageMeta>
      </SurveyHeader>

      <Formik<ISurveyFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize>
        {({
          values,
          errors,
          touched,
          submitCount,
          setFieldValue,
          setFieldTouched,
          handleSubmit,
        }) => (
          <StyledForm onSubmit={handleSubmit} noValidate>
            <ScrollToFirstError
              questions={survey.questions}
              errors={errors}
              submitCount={submitCount}
              cardRefs={cardRefs}
            />
            {survey.questions.map((question) => {
              const showError = Boolean(
                (touched[question.id] || submitCount > 0) && errors[question.id],
              );
              return (
                <QuestionCard
                  key={question.id}
                  hasError={showError}
                  ref={(el: HTMLDivElement | null) => {
                    cardRefs.current[question.id] = el;
                  }}>
                  <QuestionLabel>
                    {question.number}. {question.text}
                    {question.required ? <RequiredMark> *</RequiredMark> : null}
                  </QuestionLabel>
                  {/* A teacher-directed question is meaningless without saying
                      WHO is being rated — a group can have several teachers,
                      and the question text alone (authored in the ERP) often
                      just says «Оцените преподавателя». */}
                  {TEACHER_QUESTION_KINDS.has(question.questionType) && survey.teacherName && (
                    <QuestionSubject>
                      {t('surveys.fill.aboutTeacher', { name: survey.teacherName })}
                    </QuestionSubject>
                  )}
                  <QuestionWidget
                    question={question}
                    value={values[question.id]}
                    onChange={(value) => {
                      setFieldValue(question.id, value);
                      setFieldTouched(question.id, true, false);
                    }}
                  />
                  {showError ? <ErrorText>{errors[question.id]}</ErrorText> : null}
                </QuestionCard>
              );
            })}

            <SubmitButton type="submit" isLoading={submitting} disabled={submitting}>
              {t('surveys.fill.submit')}
            </SubmitButton>
          </StyledForm>
        )}
      </Formik>
    </SurveyPageWrapper>
  );
});

const SurveyPageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const SurveyHeader = styled.div`
  margin: 32px 0;

  ${tablet} {
    margin: 40px 0 32px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  white-space: pre-wrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  }
`;

const PageMeta = styled.div`
  margin-top: 8px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const BackButton = styled(Button)`
  width: auto;

  ${mobile} {
    width: 100%;
  }
`;

const StyledForm = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuestionCard = styled.div<{ hasError?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 20px 24px;
  border: 1px solid ${({ hasError }) => (hasError ? Colors.signal_red : Colors.additional_gray)};
  border-radius: 12px;
  background-color: ${Colors.white};
`;

const QuestionLabel = styled.div`
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.medium })}
  margin-bottom: 16px;
`;

const RequiredMark = styled.span`
  color: ${Colors.signal_red};
`;

const QuestionSubject = styled.div`
  margin: -8px 0 12px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const ErrorText = styled.div`
  margin-top: 8px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.signal_red};
`;

const VerticalCheckboxGroup = styled(Checkbox.Group)`
  && {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

// 10 buttons must survive a 375px viewport — let the strip wrap.
const NpsGroup = styled(Radio.Group)`
  && {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;

    ${mobile} {
      .ant-radio-button-wrapper {
        padding-inline: 10px;
      }
    }
  }
`;

const SubmitButton = styled(Button)`
  width: auto;
  align-self: flex-start;

  ${mobile} {
    width: 100%;
    align-self: stretch;
  }
`;
